/*
 * PROJECT DATES — Approved Baseline / Current Forecast / date Change Request (Young 27/10, Jay 28/10). The server is
 * projectChange.js; this file is its three faces:
 *   * DelayBadge — the red "Delayed" on a project row, and the "DCR-… waiting" chip beside it;
 *   * ProjectDatesModal — one project's approved vs forecast dates, the request form, and the record (every request
 *     and how each approved date was set — Young: "the approval record stays attached to the project");
 *   * DateChangeList — the approver's list on Project Approval: approve, or reject with a reason.
 * Its own overlay layer (pd-overlay, above the tracker's modals) so a button here is never under another dialog's.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from './backendConfig';
import { fmtDateTimeVN } from './timeZone';
import useEscapeClose from './useEscapeClose';
import './ProjectDates.css';

const API = `${BACKEND_URL}/api/development/project-tracker/change-requests`;
const call = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
};
const MONTH_RE = /^\d{4}-\d{2}$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const fmtMonth = (m) => (MONTH_RE.test(m || '') ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : (m || ''));
export const fmtDay = (d) => (DAY_RE.test(d || '') ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '');
const SEASON_PARTS = ['Spring', 'Summer', 'Fall', 'Winter'];
const ABBR = { Spring: 'SP', Summer: 'SU', Fall: 'FA', Winter: 'WI' };
/* the label the server writes (projectSeasonLabel) — so a proposed season reads here as it will on the project */
const seasonLabel = (year, parts) => {
  const y = Number(year) || 0;
  if (!y) return '';
  const p = SEASON_PARTS.filter((x) => parts.includes(x));
  const yy = String(y % 100).padStart(2, '0');
  if (!p.length) return String(y);
  return p.length === 1 ? yy + ABBR[p[0]] : yy + p.map((x) => x[0]).join('');
};
const fmtLog = (field, v) => (field === 'launch' ? fmtMonth(v) : field === 'season' ? (v || '') : fmtDay(v));

/* a year select and the four season boxes (at most two), as the project form has them */
function SeasonPicker({ year, parts, onChange, testid }) {
  const { t } = useTranslation();
  const now = new Date().getFullYear();
  return (
    <div className="pd-season" data-pd-season={testid}>
      <select value={year || ''} onChange={(e) => onChange({ year: e.target.value ? Number(e.target.value) : '', parts })}>
        <option value="">—</option>
        {[now - 2, now - 1, now, now + 1, now + 2, now + 3].map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      {SEASON_PARTS.map((sp) => {
        const on = parts.includes(sp);
        return (
          <label key={sp} className={`pd-season-chip${on ? ' on' : ''}`}>
            <input type="checkbox" checked={on} disabled={!on && parts.length >= 2}
              onChange={() => onChange({ year, parts: on ? parts.filter((x) => x !== sp) : [...parts, sp] })} />
            {t(`st.season${sp}`)}
          </label>
        );
      })}
      <b className="pd-season-code">{seasonLabel(year, parts)}</b>
    </div>
  );
}

export function DelayBadge({ delayed, openChangeId, needsConfirm }) {
  const { t } = useTranslation();
  if (!delayed && !openChangeId && !needsConfirm) return null;
  return (
    <>
      {needsConfirm && <span className="pd-badge pd-confirm" title={t('xDates.confirmBadgeHint')} data-pd-needs-confirm="1">{t('xDates.confirmBadge')}</span>}
      {delayed && <span className="pd-badge pd-late" title={t('xDates.delayedTitle')} data-pd-delayed="1">{t('xDates.delayed')}</span>}
      {openChangeId && <span className="pd-badge pd-wait" title={t('xDates.changePendingTitle')}>{t('xDates.changePending', { id: openChangeId })}</span>}
    </>
  );
}

const StatusPill = ({ status }) => {
  const { t } = useTranslation();
  return <span className={`pd-badge pd-st-${String(status).toLowerCase()}`}>{t(`xDates.status_${status}`)}</span>;
};

/* the dates a request is about: approved → proposed, with today's forecast beside it */
function RequestDates({ c }) {
  const { t } = useTranslation();
  return (
    <table className="pd-table pd-table-sm">
      <thead><tr><th>{t('xDates.colWhat')}</th><th>{t('xDates.from')}</th><th>{t('xDates.to')}</th><th>{t('xDates.forecastNow')}</th></tr></thead>
      <tbody>
        {c.proposedSeason && (
          <tr><td>{t('xDates.season')}</td><td>{c.baselineSeason || t('xDates.noBaselineYet')}</td>
            <td><b>{c.proposedSeason}</b></td><td>{c.currentSeason || t('xDates.none')}</td></tr>
        )}
        {c.proposedLaunch && (
          <tr><td>{t('xDates.launch')}</td><td>{fmtMonth(c.baselineLaunch) || t('xDates.noBaselineYet')}</td>
            <td><b>{fmtMonth(c.proposedLaunch)}</b></td><td>{fmtMonth(c.forecastLaunch) || t('xDates.none')}</td></tr>
        )}
        {c.skus.map((s) => (
          <tr key={s.projectId}><td>{t('xDates.exfOf', { id: s.projectId })}{s.itemName ? <small> · {s.itemName}</small> : null}</td>
            <td>{fmtDay(s.baselineExf) || t('xDates.noBaselineYet')}</td><td><b>{fmtDay(s.proposedExf)}</b></td><td>{fmtDay(s.forecastExf) || t('xDates.none')}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

function RequestText({ c }) {
  const { t } = useTranslation();
  const rows = [['reason', c.reason], ['impactSales', c.impactSales], ['impactOrders', c.impactOrders],
    ['impactProduction', c.impactProduction], ['keepOptions', c.keepOptions]].filter(([, v]) => v);
  return (
    <dl className="pd-dl">
      {rows.map(([k, v]) => <React.Fragment key={k}><dt>{t(`xDates.${k}`)}</dt><dd>{v}</dd></React.Fragment>)}
    </dl>
  );
}

export function ProjectDatesModal({ groupId, canRequest, onClose, onChanged }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEscapeClose(onClose);

  const load = useCallback(async () => {
    try { setError(''); setD(await call(`/project/${encodeURIComponent(groupId)}`)); } catch (e) { setError(e.message); }
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  const approvedSkus = useMemo(() => (d ? d.skus.filter((s) => s.approved) : []), [d]);
  const startForm = () => {
    // a late date is the likely one to move: proposed starts at the forecast there, empty elsewhere
    setForm({
      proposedLaunch: d.launch.late && MONTH_RE.test(d.launch.forecast) ? d.launch.forecast : '',
      // the season starts as the approved one: changing it is a deliberate act
      seasonYear: d.season.baselineYear || '', seasonParts: String(d.season.baselineParts || '').split(',').filter(Boolean),
      skus: Object.fromEntries(approvedSkus.map((s) => [s.projectId, s.late && s.forecastExf ? s.forecastExf : ''])),
      reason: '', impactSales: '', impactOrders: '', impactProduction: '', keepOptions: '',
    });
    setMsg('');
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true); setError('');
    try {
      const r = await call('', { method: 'POST', body: JSON.stringify({
        groupId, reason: form.reason, impactSales: form.impactSales, impactOrders: form.impactOrders, impactProduction: form.impactProduction,
        keepOptions: form.keepOptions, proposedLaunch: form.proposedLaunch,
        ...(seasonLabel(form.seasonYear, form.seasonParts) && seasonLabel(form.seasonYear, form.seasonParts) !== d.season.baseline
          ? { proposedSeasonYear: form.seasonYear, proposedSeasonParts: form.seasonParts } : {}),
        skus: Object.entries(form.skus).filter(([, v]) => v).map(([projectId, proposedExf]) => ({ projectId, proposedExf })),
      }) });
      setForm(null); setMsg(t('xDates.sent', { id: r.request.crId }));
      await load(); onChanged?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const withdraw = async (id) => {
    if (!window.confirm(t('xDates.withdrawConfirm', { id }))) return;
    try { await call(`/${encodeURIComponent(id)}/withdraw`, { method: 'POST' }); await load(); onChanged?.(); } catch (e) { setError(e.message); }
  };
  const stateOf = (late, done) => (done ? <span className="pd-muted">{t('xDates.done')}</span>
    : late ? <span className="pd-badge pd-late">{t('xDates.delayed')}</span> : <span className="pd-ok">{t('xDates.onTrack')}</span>);
  const logLine = (l) => (l.source === 'change_request' ? t('xDates.logCr', { id: l.cr_id })
    : l.source === 'go_live' ? t('xDates.logGoLive') : l.source === 'original' ? t('xDates.logOriginal') : t('xDates.logFirst'));
  /* THE OWNER'S ORIGINAL TARGET (Jay 28/10) — once per project, from where it was agreed */
  const [orig, setOrig] = useState(null);
  const startOrig = () => setOrig({
    launchMonth: MONTH_RE.test(d.launch.baseline) ? d.launch.baseline : '',
    seasonYear: d.season.baselineYear || d.season.currentYear || '',
    seasonParts: String(d.season.baselineParts || d.season.currentParts || '').split(',').filter(Boolean),
    note: '',
  });
  const confirmOrig = async () => {
    setBusy(true); setError('');
    try {
      await call(`/project/${encodeURIComponent(groupId)}/confirm`, { method: 'POST', body: JSON.stringify(orig) });
      setOrig(null); setMsg(t('xDates.confirmDone'));
      await load(); onChanged?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="pd-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pd-modal" role="dialog" aria-modal="true" aria-label={t('xDates.title', { id: groupId })} data-pd-modal="1">
        <div className="pd-head">
          <div><b>{t('xDates.title', { id: groupId })}</b>{d ? <span className="pd-head-name">{d.groupName}</span> : null}</div>
          <button type="button" className="pd-x" onClick={onClose} aria-label={t('xDates.close')}>×</button>
        </div>
        <div className="pd-body">
          {!d && !error && <div className="pd-muted">{t('xDates.loading')}</div>}
          {error && <div className="pd-error" role="alert">{error}</div>}
          {msg && <div className="pd-msg">{msg}</div>}
          {d && (
            <>
              <p className="pd-rule">{t('xDates.rule')}</p>
              <table className="pd-table" data-pd-dates="1">
                <thead><tr><th>{t('xDates.colWhat')}</th><th>{t('xDates.colBaseline')}</th><th>{t('xDates.colForecast')}</th><th>{t('xDates.colState')}</th></tr></thead>
                <tbody>
                  <tr className={d.launch.late ? 'pd-row-late' : ''}>
                    <td>{t('xDates.launch')}</td>
                    <td><b>{fmtMonth(d.launch.baseline) || <span className="pd-muted">{t('xDates.noBaselineYet')}</span>}</b></td>
                    <td>{fmtMonth(d.launch.forecast) || t('xDates.none')}</td>
                    <td>{d.launch.baseline ? stateOf(d.launch.late, false) : null}</td>
                  </tr>
                  {(d.season.baseline || d.season.current) && (
                    <tr className={d.season.late ? 'pd-row-late' : ''} data-pd-season-row="1">
                      <td>{t('xDates.season')}</td>
                      <td><b>{d.season.baseline || <span className="pd-muted">{t('xDates.noBaselineYet')}</span>}</b></td>
                      <td>{d.season.current || t('xDates.none')}</td>
                      <td>{d.season.baseline ? stateOf(d.season.late, false) : null}</td>
                    </tr>
                  )}
                  {approvedSkus.map((s) => (
                    <tr key={s.projectId} className={s.late ? 'pd-row-late' : ''}>
                      <td>{t('xDates.exfOf', { id: s.projectId })}{s.itemName ? <small> · {s.itemName}</small> : null}</td>
                      <td><b>{fmtDay(s.baselineExf) || <span className="pd-muted">{t('xDates.noBaselineYet')}</span>}</b></td>
                      <td>{fmtDay(s.forecastExf) || t('xDates.none')}</td>
                      <td>{s.baselineExf ? stateOf(s.late, ['Complete', 'Dropped'].includes(s.status)) : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!approvedSkus.length && <p className="pd-muted">{t('xDates.noSkus')}</p>}

              {d.confirm.at && (
                <p className="pd-muted" data-pd-confirmed="1">{t('xDates.confirmedBy', { by: d.confirm.by, at: fmtDateTimeVN(d.confirm.at), note: d.confirm.note })}</p>
              )}
              {d.confirm.needed && !orig && (
                <div className="pd-owed" data-pd-owed="1">
                  <div><b>{t('xDates.confirmTitle')}</b><div>{t('xDates.confirmWhy')}</div></div>
                  {canRequest && <button type="button" className="pd-btn pd-btn-primary" onClick={startOrig} data-pd-confirm-start="1">{t('xDates.confirmStart')}</button>}
                </div>
              )}
              {orig && (
                <div className="pd-form" data-pd-confirm-form="1">
                  <h4>{t('xDates.confirmTitle')}</h4>
                  <p className="pd-muted">{t('xDates.confirmHint')}</p>
                  <div className="pd-grid">
                    <label>{t('xDates.origLaunch')} *
                      <input type="month" value={orig.launchMonth} onChange={(e) => setOrig((o) => ({ ...o, launchMonth: e.target.value }))} data-pd-orig-launch="1" />
                    </label>
                  </div>
                  {(d.season.current || d.season.baseline) && (
                    <label className="pd-full">{t('xDates.origSeason')} *
                      <SeasonPicker year={orig.seasonYear} parts={orig.seasonParts} testid="orig"
                        onChange={({ year, parts }) => setOrig((o) => ({ ...o, seasonYear: year, seasonParts: parts }))} />
                    </label>
                  )}
                  <label className="pd-full">{t('xDates.origSource')} *
                    <textarea rows={2} value={orig.note} placeholder={t('xDates.origSourcePh')} onChange={(e) => setOrig((o) => ({ ...o, note: e.target.value }))} data-pd-orig-note="1" />
                  </label>
                  <div className="pd-actions">
                    <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setOrig(null)} disabled={busy}>{t('xDates.cancel')}</button>
                    <button type="button" className="pd-btn pd-btn-primary" onClick={confirmOrig} disabled={busy} data-pd-orig-save="1">{busy ? t('xDates.working') : t('xDates.confirmSave')}</button>
                  </div>
                </div>
              )}

              {d.open && (
                <div className="pd-open">
                  <div>{t('xDates.openBox', { id: d.open.crId, by: d.open.requestedBy, at: fmtDateTimeVN(d.open.requestedAt) })}</div>
                  {canRequest && <button type="button" className="pd-btn pd-btn-ghost" onClick={() => withdraw(d.open.crId)}>{t('xDates.withdraw')}</button>}
                </div>
              )}
              {canRequest && !d.open && !form && (
                <div className="pd-actions"><button type="button" className="pd-btn pd-btn-primary" data-pd-new="1" onClick={startForm}>{t('xDates.newRequest')}</button></div>
              )}

              {form && (
                <div className="pd-form" data-pd-form="1">
                  <h4>{t('xDates.newTitle')}</h4>
                  <p className="pd-muted">{t('xDates.newHint')}</p>
                  <div className="pd-grid">
                    <label>{t('xDates.proposedLaunch')}
                      <input type="month" value={form.proposedLaunch} onChange={set('proposedLaunch')} data-pd-launch="1" />
                    </label>
                    {(d.season.baseline || d.season.current) && (
                      <label className="pd-span2">{t('xDates.proposedSeason')}
                        <SeasonPicker year={form.seasonYear} parts={form.seasonParts} testid="proposed"
                          onChange={({ year, parts }) => setForm((f) => ({ ...f, seasonYear: year, seasonParts: parts }))} />
                      </label>
                    )}
                    {approvedSkus.map((s) => (
                      <label key={s.projectId}>{t('xDates.proposedExf')} · {s.projectId}
                        <input type="date" value={form.skus[s.projectId] || ''} data-pd-exf={s.projectId}
                          onChange={(e) => setForm((f) => ({ ...f, skus: { ...f.skus, [s.projectId]: e.target.value } }))} />
                      </label>
                    ))}
                  </div>
                  <label className="pd-full">{t('xDates.reason')} *<textarea rows={2} value={form.reason} onChange={set('reason')} data-pd-reason="1" /></label>
                  <div className="pd-grid3">
                    <label>{t('xDates.impactSales')}<textarea rows={2} value={form.impactSales} onChange={set('impactSales')} data-pd-impact="sales" /></label>
                    <label>{t('xDates.impactOrders')}<textarea rows={2} value={form.impactOrders} onChange={set('impactOrders')} /></label>
                    <label>{t('xDates.impactProduction')}<textarea rows={2} value={form.impactProduction} onChange={set('impactProduction')} /></label>
                  </div>
                  <small className="pd-muted">{t('xDates.impactHint')}</small>
                  <label className="pd-full">{t('xDates.keepOptions')} *<textarea rows={2} value={form.keepOptions} onChange={set('keepOptions')} data-pd-keep="1" /></label>
                  <div className="pd-actions">
                    <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setForm(null)} disabled={busy}>{t('xDates.cancel')}</button>
                    <button type="button" className="pd-btn pd-btn-primary" onClick={submit} disabled={busy} data-pd-send="1">{busy ? t('xDates.sending') : t('xDates.send')}</button>
                  </div>
                </div>
              )}

              <h4 className="pd-sub">{t('xDates.history')}</h4>
              {!d.requests.length && <p className="pd-muted">{t('xDates.noHistory')}</p>}
              {d.requests.map((c) => (
                <div key={c.crId} className="pd-card" data-pd-request={c.crId}>
                  <div className="pd-card-head"><b>{c.crId}</b> <StatusPill status={c.status} />
                    <span className="pd-muted">{t('xDates.askedBy', { by: c.requestedBy, at: fmtDateTimeVN(c.requestedAt) })}</span>
                    {c.decidedAt && <span className="pd-muted">{t('xDates.decidedBy', { status: t(`xDates.status_${c.status}`), by: c.decidedBy, at: fmtDateTimeVN(c.decidedAt) })}</span>}
                  </div>
                  <RequestDates c={c} />
                  <RequestText c={c} />
                  {c.decisionNote && <div className="pd-note">{c.decisionNote}</div>}
                </div>
              ))}
              {d.log.length > 0 && (
                <>
                  <h4 className="pd-sub">{t('xDates.logTitle')}</h4>
                  <ul className="pd-log">
                    {d.log.map((l, i) => (
                      <li key={i}>{fmtDateTimeVN(l.at)} · {l.field === 'launch' ? t('xDates.launch') : l.field === 'season' ? t('xDates.season') : t('xDates.exfOf', { id: l.project_id })}:{' '}
                        {l.old_value ? `${fmtLog(l.field, l.old_value)} → ` : ''}
                        <b>{fmtLog(l.field, l.new_value)}</b> — {logLine(l)}{l.by_name && l.source !== 'go_live' ? ` (${l.by_name})` : ''}</li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* THE APPROVER'S LIST — on Project Approval, above the projects waiting, so everything waiting for the CEO is one
   screen (Jay: "One list of everything waiting for him, approve or reject, a reason box on reject"). A TABLE, one row a
   request, since Max asked (28/10) what it looks like with many: the change and how far it moves read on the row, the
   reason / impact / options open under it, and ticked rows are approved or rejected together (one reason for a bulk
   reject) — each request still gets its own decision, log and mail. The furthest move comes first. */
const monthIndex = (m) => (MONTH_RE.test(m || '') ? Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) : null);
const dayIndex = (d) => (DAY_RE.test(d || '') ? Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10))) / 86400000 : null);
/* how far a request moves the approved dates, in days (a month counts 30.4); null when nothing was approved before */
const shiftOf = (c) => {
  const out = [];
  const a = monthIndex(c.baselineLaunch); const b = monthIndex(c.proposedLaunch);
  if (c.proposedLaunch && a !== null && b !== null) out.push((b - a) * 30.4);
  c.skus.forEach((s) => { const x = dayIndex(s.baselineExf); const y = dayIndex(s.proposedExf); if (x !== null && y !== null) out.push(y - x); });
  return out.length ? out.reduce((m, v) => (Math.abs(v) > Math.abs(m) ? v : m), 0) : null;
};
function ShiftText({ days }) {
  const { t } = useTranslation();
  if (days === null || days === undefined) return <span className="pd-muted">{t('xDates.none')}</span>;
  const wk = Math.round(days / 7);
  if (wk === 0) return <span className="pd-muted">{t('xDates.shiftDays', { n: Math.round(days) })}</span>;
  return <b className={wk > 0 ? 'pd-shift-late' : 'pd-shift-early'}>{wk > 0 ? t('xDates.shiftWk', { n: wk }) : t('xDates.shiftEarlier', { n: -wk })}</b>;
}

export function DateChangeList({ canDecide, onDecided, onCount }) {
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [picked, setPicked] = useState([]);   // crIds ticked
  const [openRow, setOpenRow] = useState({}); // crId -> details shown
  const [pending, setPending] = useState(null); // { ids, kind: 'approve' | 'reject' } — the decision being confirmed
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [team, setTeam] = useState('');
  const [q, setQ] = useState('');
  const load = useCallback(async () => {
    try { const r = (await call('?status=Pending')).requests; setList(r); onCount?.(r.length); } catch (e) { setError(e.message); }
  }, [onCount]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (list || []).map((c) => ({ ...c, shift: shiftOf(c) }))
      .filter((c) => (!team || c.teamName === team)
        && (!needle || [c.crId, c.groupId, c.groupName, c.teamName, c.requestedBy, c.reason, ...c.skus.map((x) => x.projectId)]
          .some((v) => String(v || '').toLowerCase().includes(needle))))
      .sort((a, b) => (b.shift ?? -1e9) - (a.shift ?? -1e9) || String(a.requestedAt).localeCompare(String(b.requestedAt)));
  }, [list, team, q]);
  const teams = useMemo(() => [...new Set((list || []).map((c) => c.teamName).filter(Boolean))].sort(), [list]);
  const visibleIds = rows.map((c) => c.crId);
  const pickedVisible = picked.filter((id) => visibleIds.includes(id));
  const allTicked = visibleIds.length > 0 && pickedVisible.length === visibleIds.length;

  const ask = (ids, kind) => { setPending({ ids, kind }); setNote(''); setMsg(''); setError(''); };
  const decide = async () => {
    if (!pending || (pending.kind === 'reject' && !note.trim())) return;
    setBusy(true); setError('');
    let ok = 0; const failed = [];
    for (const id of pending.ids) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await call(`/${encodeURIComponent(id)}/${pending.kind}`, { method: 'POST', body: JSON.stringify({ note }) });
        ok += 1;
      } catch (e) { failed.push(`${id}: ${e.message}`); }
    }
    // the list is read again BEFORE the message, so a row just decided is never on screen to be pressed a second time
    await load();
    setMsg(ok ? t(pending.kind === 'approve' ? 'xDates.doneApproved' : 'xDates.doneRejected', { n: ok }) : '');
    if (failed.length) setError(t('xDates.failed', { n: failed.length, err: failed.join(' · ') }));
    setPending(null); setNote(''); setPicked([]); setBusy(false);
    onDecided?.();
  };

  if (!list) return error ? <div className="pd-error">{error}</div> : null;
  /* drawn even when nothing waits — Jay (28/10) looked for it on an empty day and could not tell it existed */
  const changeLines = (c) => [
    ...(c.proposedSeason ? [`${t('xDates.season')} ${c.baselineSeason || t('xDates.none')} → ${c.proposedSeason}`] : []),
    ...(c.proposedLaunch ? [`${t('xDates.launchShort')} ${fmtMonth(c.baselineLaunch) || t('xDates.none')} → ${fmtMonth(c.proposedLaunch)}`] : []),
    ...(c.skus.length === 1 ? [`EXF ${c.skus[0].projectId} ${fmtDay(c.skus[0].baselineExf) || t('xDates.none')} → ${fmtDay(c.skus[0].proposedExf)}`]
      : c.skus.length ? [t('xDates.exfMany', { n: c.skus.length })] : []),
  ];
  const cols = canDecide ? 9 : 7;
  return (
    <section className="pa-section pd-section" data-pd-list="1">
      <h3 className="pd-section-title">{t('xDates.listTitle')} <span className="pd-count">{list.length}</span></h3>
      {list.length > 0 && <p className="pd-muted pd-list-hint">{t('xDates.listHint')}</p>}
      {!list.length && <p className="pd-muted pd-list-hint" data-pd-empty="1">{t('xDates.listEmpty')}</p>}
      {msg && <div className="pd-msg">{msg}</div>}
      {error && <div className="pd-error" role="alert">{error}</div>}
      {list.length > 0 && (
        <div className="pa-toolbar">
          <span className="pa-count-text">{rows.length === list.length ? t('xDates.countAll', { n: rows.length }) : t('xDates.countOf', { n: rows.length, total: list.length })}</span>
          {teams.length > 1 && (
            <select className="pa-scope" value={team} onChange={(e) => setTeam(e.target.value)} data-pd-team="1">
              <option value="">{t('xDates.allTeams')}</option>
              {teams.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          )}
          <input type="search" className="pa-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xDates.searchPh')} aria-label={t('xDates.searchPh')} data-pd-search="1" />
          {canDecide && pickedVisible.length > 0 && !pending && (
            <span className="pd-bulk">
              <button type="button" className="pa-btn approve" data-pd-bulk="approve" onClick={() => ask(pickedVisible, 'approve')}>{t('xDates.approveSel', { n: pickedVisible.length })}</button>
              <button type="button" className="pa-btn reject" data-pd-bulk="reject" onClick={() => ask(pickedVisible, 'reject')}>{t('xDates.rejectSel', { n: pickedVisible.length })}</button>
              <button type="button" className="pa-btn" onClick={() => setPicked([])}>{t('xDates.clearSel')}</button>
            </span>
          )}
        </div>
      )}
      {pending && (
        <div className={`pd-decide-bar ${pending.kind}`} data-pd-decide="1">
          <b>{t(pending.kind === 'approve' ? 'xDates.decideApprove' : 'xDates.decideReject', { n: pending.ids.length, ids: pending.ids.join(', ') })}</b>
          <label className="pd-full">{t(pending.kind === 'reject' ? 'xDates.rejectReason' : 'xDates.approveNote')}
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-pd-note="1" autoFocus />
          </label>
          <div className="pd-actions">
            <button type="button" className="pa-btn" onClick={() => { setPending(null); setNote(''); }} disabled={busy}>{t('xDates.cancel')}</button>
            <button type="button" className={`pa-btn ${pending.kind === 'reject' ? 'reject' : 'approve'}`} data-pd-confirm={pending.kind}
              disabled={busy || (pending.kind === 'reject' && !note.trim())} onClick={decide}>
              {busy ? t('xDates.working') : t(pending.kind === 'reject' ? 'xDates.confirmReject' : 'xDates.confirmApprove')}</button>
          </div>
        </div>
      )}
      {list.length > 0 && (
        <div className="pa-table-wrap pd-list-scroll">
          <table className="pa-table pd-list-table">
            <thead>
              <tr>
                {canDecide && <th className="pd-col-check"><input type="checkbox" checked={allTicked} aria-label={t('xDates.tickAll')}
                  onChange={(e) => setPicked(e.target.checked ? visibleIds : [])} data-pd-tickall="1" /></th>}
                <th>{t('xDates.colRequest')}</th>
                <th>{t('xDates.colProject')}</th>
                <th>{t('xDates.colTeam')}</th>
                <th>{t('xDates.colChange')}</th>
                <th>{t('xDates.colMoves')}</th>
                <th>{t('xDates.colAsked')}</th>
                <th>{t('xDates.colReason')}</th>
                {canDecide && <th>{t('xDates.colAction')}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <React.Fragment key={c.crId}>
                  <tr data-pd-request={c.crId} className={picked.includes(c.crId) ? 'pd-picked' : ''}>
                    {canDecide && <td className="pd-col-check"><input type="checkbox" checked={picked.includes(c.crId)} aria-label={c.crId}
                      onChange={(e) => setPicked((l) => (e.target.checked ? [...l, c.crId] : l.filter((x) => x !== c.crId)))} /></td>}
                    <td className="mono">
                      <button type="button" className="pd-expand" aria-expanded={!!openRow[c.crId]} data-pd-expand={c.crId}
                        onClick={() => setOpenRow((o) => ({ ...o, [c.crId]: !o[c.crId] }))}>{openRow[c.crId] ? '▾' : '▸'}</button>{c.crId}
                    </td>
                    <td><span className="mono">{c.groupId}</span> <b>{c.groupName}</b></td>
                    <td>{c.teamName || t('xDates.none')}</td>
                    <td className="pd-change">{changeLines(c).map((l) => <div key={l}>{l}</div>)}</td>
                    <td className="pa-when"><ShiftText days={c.shift} /></td>
                    <td className="pa-when">{c.requestedBy}<div className="pd-muted">{fmtDateTimeVN(c.requestedAt)}</div></td>
                    <td className="pd-reason" title={c.reason}>{c.reason.length > 90 ? `${c.reason.slice(0, 90)}…` : c.reason}</td>
                    {canDecide && (
                      <td className="pa-when">
                        <div className="pd-row-btns">
                          <button type="button" className="pa-btn approve" data-pd-approve={c.crId} disabled={!!pending} onClick={() => ask([c.crId], 'approve')}>{t('xDates.approve')}</button>
                          <button type="button" className="pa-btn reject" data-pd-reject={c.crId} disabled={!!pending} onClick={() => ask([c.crId], 'reject')}>{t('xDates.reject')}</button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {openRow[c.crId] && (
                    <tr className="pd-detail-row"><td colSpan={cols}><div className="pd-detail"><RequestDates c={c} /><RequestText c={c} /></div></td></tr>
                  )}
                </React.Fragment>
              ))}
              {!rows.length && <tr><td colSpan={cols} className="pd-muted">{t('xDates.noMatch')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
